import React, { useState } from "react";
import {
  Button,
  Container,
  FormHelperText,
  Grid,
  InputLabel,
  OutlinedInput,
  Stack,
  Typography,
  Alert,
  Collapse,
  Box,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ContactData } from "../../types/ContactType";
import { REQUIRED_FIELD, EMAIL_FIELD } from "../../utils/constants";
import ReactGA from "react-ga4";
import PhoneMask from "../InputMask/PhoneMask";
import "./ContactSection.scss";

const ContactSection = () => {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [alert, setAlert] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const schemaContact = yup.object({
    nome: yup.string().required(REQUIRED_FIELD),
    email: yup.string().email(EMAIL_FIELD).required(REQUIRED_FIELD),
    telefone: yup.string().required(REQUIRED_FIELD),
    mensagem: yup.string().required(REQUIRED_FIELD).min(10, "Mensagem deve ter pelo menos 10 caracteres"),
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schemaContact),
  });

  const onSubmitContact = async (data: ContactData) => {
    setLoading(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}contact`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        setAlert({ type: "error", message: errorData.message || "Erro ao enviar mensagem. Tente novamente." });
        setOpen(true);
        throw new Error("Network response was not ok");
      }

      setAlert({ type: "success", message: "Mensagem enviada com sucesso! Entraremos em contato em breve." });
      setOpen(true);
      reset();

      // Google Analytics tracking
      ReactGA.event("contact_form_submit", {
        event: 'contact_form_submit',
        button_id: 'contact_submit',
        page_path: window.location.pathname
      });

    } catch (error) {
      console.error("Erro ao enviar o formulário:", error);
      if (!alert) {
        setAlert({ type: "error", message: "Erro ao enviar mensagem. Verifique sua conexão e tente novamente." });
        setOpen(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ContactSection">
      <Container maxWidth="sm" className="MainContainer">
        {alert && (
          <Collapse in={open} sx={{ marginTop: "100px" }}>
            <Alert
              variant="filled"
              severity={alert.type}
              onClose={() => setOpen(false)}
            >
              {alert.message}
            </Alert>
          </Collapse>
        )}

        <Typography variant="h1" sx={{ paddingTop: open ? "50px" : "150px" }}>
          Fale Conosco
        </Typography>
        <Typography variant="h3" className="Subtitle">
          Entre em contato conosco. Estamos aqui para ajudar!
        </Typography>

        <form onSubmit={handleSubmit(onSubmitContact)}>
          <Grid container mt={"50px"} spacing={2}>
            <Grid item xs={12}>
              <Stack spacing={1}>
                <Controller
                  name="nome"
                  control={control}
                  defaultValue={""}
                  render={({ field }) => (
                    <>
                      <InputLabel htmlFor="nome">Nome completo</InputLabel>
                      <OutlinedInput
                        {...field}
                        id="nome"
                        type="text"
                        placeholder="Digite seu nome completo"
                        fullWidth
                        error={!!errors.nome}
                      />
                      <FormHelperText sx={{ color: "#f44336" }}>
                        {errors.nome?.message}
                      </FormHelperText>
                    </>
                  )}
                />
              </Stack>
            </Grid>

            <Grid item xs={12}>
              <Stack spacing={1}>
                <Controller
                  name="email"
                  control={control}
                  defaultValue={""}
                  render={({ field }) => (
                    <>
                      <InputLabel htmlFor="email">E-mail</InputLabel>
                      <OutlinedInput
                        {...field}
                        id="email"
                        type="email"
                        placeholder="seu-email@exemplo.com"
                        fullWidth
                        error={!!errors.email}
                      />
                      <FormHelperText sx={{ color: "#f44336" }}>
                        {errors.email?.message}
                      </FormHelperText>
                    </>
                  )}
                />
              </Stack>
            </Grid>

            <Grid item xs={12}>
              <Stack spacing={1}>
                <Controller
                  name="telefone"
                  control={control}
                  defaultValue={""}
                  render={({ field }) => (
                    <>
                      <InputLabel htmlFor="telefone">Telefone</InputLabel>
                      <OutlinedInput
                        {...field}
                        id="telefone"
                        type="tel"
                        placeholder="(11) 99999-9999"
                        fullWidth
                        error={!!errors.telefone}
                        inputComponent={PhoneMask as any}
                      />
                      <FormHelperText sx={{ color: "#f44336" }}>
                        {errors.telefone?.message}
                      </FormHelperText>
                    </>
                  )}
                />
              </Stack>
            </Grid>

            <Grid item xs={12}>
              <Stack spacing={1}>
                <Controller
                  name="mensagem"
                  control={control}
                  defaultValue={""}
                  render={({ field }) => (
                    <>
                      <InputLabel htmlFor="mensagem">Mensagem</InputLabel>
                      <OutlinedInput
                        {...field}
                        id="mensagem"
                        multiline
                        rows={4}
                        placeholder="Digite sua mensagem aqui..."
                        fullWidth
                        error={!!errors.mensagem}
                      />
                      <FormHelperText sx={{ color: "#f44336" }}>
                        {errors.mensagem?.message}
                      </FormHelperText>
                    </>
                  )}
                />
              </Stack>
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                className="SendButton"
                fullWidth
                type="submit"
                disabled={loading}
                onClick={() => {
                  ReactGA.event("contact_form_click", {
                    event: 'contact_form_click',
                    button_id: 'contact_submit',
                    page_path: window.location.pathname
                  });
                }}
              >
                {loading ? "Enviando..." : "Enviar Mensagem"}
              </Button>
            </Grid>
          </Grid>
        </form>

        {/* Botão do WhatsApp */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#666' }}>
            Ou entre em contato diretamente pelo WhatsApp
          </Typography>
          <Button
            variant="contained"
            className="WhatsAppButton"
            onClick={() => {
              // Google Analytics tracking
              ReactGA.event("whatsapp_contact_click", {
                event: 'whatsapp_contact_click',
                button_id: 'whatsapp_contact',
                page_path: window.location.pathname
              });

              // Abrir WhatsApp
              window.open(
                'https://wa.me/5511942778858?text=Olá!%20Preciso%20de%20ajuda%20com%20meu%20plano%20WashPass.',
                '_blank'
              );
            }}
          >
            💬 Falar no WhatsApp
          </Button>
        </Box>
      </Container>
    </div>
  );
};

export default ContactSection;
